import { useCallback, useEffect, useReducer, useRef } from "react";
import { CollapseArrow, ExpandArrow } from "src/components/UI/Arrow/Arrow";
import { NavLinkWithQuery } from "src/components/UI/NavLinkWithQuery/NavLinkWithQuery";
import classes from "./ExpandingNavList.module.css";
import { useTabDirectionFocus } from "src/hooks/useTabDirectionFocus";

const listStateReducer = (prevState, action) => {
  if (action.type === "toggle") {
    let newState = [...prevState];
    for (let i = 0; i < newState.length; i++) {
      if (i === action.payload.itemNum) {
        newState[i].expanded = action.payload.value;
      } else {
        newState[i].expanded = false;
      }
    }
    return newState;
  }
};

function ExpandingNavList(props) {
  const [state, dispatch] = useReducer(
    listStateReducer,
    props.items.map((item) => {
      return { ...item, expanded: false };
    })
  );

  const onToggleHandler = useCallback(
    (i) => {
      dispatch({
        type: "toggle",
        payload: { itemNum: i, value: !state[i].expanded },
      });
    },
    [state]
  );

  const tabFocus = useTabDirectionFocus();
  const listRef = useRef(Array.from({ length: props.items.length }, (_, i) => null));

  useEffect(() => {
    const elements = listRef.current || [];
    const handlers = [];

    elements.forEach((el, i) => {
      if (!el) return;

      const onFocus = (e) => {
        if (tabFocus.current) {
          onToggleHandler(i);
        }
      };

      el.addEventListener("focus", onFocus);
      handlers.push({ el, onFocus });
    });

    return () =>
      handlers.forEach(({ el, onFocus }) => el.removeEventListener("focus", onFocus));
  }, [onToggleHandler, tabFocus]);

  const renderListItem = (item, i) => {
    const outerItem = (
      <li
        key={item.name}
        id={item.name}
        ref={(el) => {
          listRef.current[i] = el;
        }}
        tabIndex="0"
        className={`${classes.child} ${classes.border} ${
          state[i].expanded ? classes.none : ""
        }`}
        onClick={(e) => onToggleHandler(i)}>
        <span>{props.uppercase ? item.name.toUpperCase() : item.name}</span>
        {state[i].expanded ? <CollapseArrow /> : <ExpandArrow />}
      </li>
    );

    const innerItems = state[i].expanded ? (
      <ul key={item.name + "_sublist"} className={`${classes.container} ${classes.border}`}>
        {item.items.map((innerItem, y) => (
          <li key={`${item.name + "_item"}${y}`} className={classes.link}>
            <NavLinkWithQuery to={innerItem.to}>{innerItem.name}</NavLinkWithQuery>
          </li>
        ))}
      </ul>
    ) : (
      []
    );

    return [outerItem, innerItems];
  };

  return (
    <ul className={`${classes.parent} ${props.className}`}>
      {state.map((item, i) => renderListItem(item, i))}
    </ul>
  );
}

export default ExpandingNavList;
